import type { $Fetch } from 'ofetch';
import { BaseClient } from '@shared/clients/BaseClient.ts';
import type {
    CrocotileGetVariationSuccess,
    CrocotileGetVariationV2Success,
    CrocotileListVariationsSuccess,
    CrocotileListVariationsV2Success,
    Logger,
    Variation,
} from '@shared/types/types';
import type { AsyncResult, Result } from '@shared/types/utilities';

const DEFAULT_TIMEOUT_MS = 160;

export class CrocotileClient extends BaseClient {
    constructor({
        fetchClient,
        logger,
        baseURL,
        timeout = DEFAULT_TIMEOUT_MS,
    }: {
        fetchClient: $Fetch;
        logger: Logger;
        baseURL?: string;
        timeout?: number;
    }) {
        super(baseURL, fetchClient, logger, timeout);
    }

    async getVariations(
        variationIds: string[],
        options?: { apiVersion?: 1 },
    ): AsyncResult<CrocotileListVariationsSuccess>;
    async getVariations(
        variationIds: string[],
        options: { apiVersion: 2 },
    ): AsyncResult<CrocotileListVariationsV2Success>;
    async getVariations(
        variationIds: string[],
        options?: { apiVersion?: 1 | 2 },
    ): Promise<
        Awaited<AsyncResult<CrocotileListVariationsSuccess>> | Awaited<AsyncResult<CrocotileListVariationsV2Success>>
    > {
        if (!variationIds.length) {
            return { state: 'success', payload: [] };
        }

        const query = { variationIds: variationIds.join(',') };
        const headers: Record<string, string> = { accept: 'application/json' };
        if (options?.apiVersion === 2) {
            headers['crocotile-version'] = '2';
        }

        try {
            const payload = await this.fetch<CrocotileListVariationsSuccess | CrocotileListVariationsV2Success>(
                '/crocotile/tile/data',
                { query, headers },
            );
            const result: Result<CrocotileListVariationsSuccess> | Result<CrocotileListVariationsV2Success> = {
                state: 'success',
                payload,
            };
            return result;
        } catch (_err: unknown) {
            return {
                state: 'error',
            } satisfies Result<CrocotileListVariationsSuccess>;
        }
    }

    async getVariation(
        id: Variation['variationId'],
        options?: { apiVersion?: 1 },
    ): AsyncResult<CrocotileGetVariationSuccess>;
    async getVariation(
        id: Variation['variationId'],
        options: { apiVersion: 2 },
    ): AsyncResult<CrocotileGetVariationV2Success>;
    async getVariation(
        id: Variation['variationId'],
        options?: { apiVersion?: 1 | 2 },
    ): Promise<
        Awaited<AsyncResult<CrocotileGetVariationSuccess>> | Awaited<AsyncResult<CrocotileGetVariationV2Success>>
    > {
        const headers: Record<string, string> = {};
        if (options?.apiVersion === 2) {
            headers['crocotile-version'] = '2';
        }

        try {
            const payload = await this.fetch<CrocotileGetVariationSuccess | CrocotileGetVariationV2Success>(
                `/crocotile/tile/data/${id}`,
                { headers },
            );
            const result: Result<CrocotileGetVariationSuccess> | Result<CrocotileGetVariationV2Success> = {
                state: 'success',
                payload,
            };
            return result;
        } catch (_err: unknown) {
            return {
                state: 'error',
            } satisfies Result<CrocotileGetVariationSuccess>;
        }
    }
}
